// apps/mtd-itsa-web/src/hooks/use-fraud-context.ts
// this hook collects device/user data needed for HMRC Gov‑Client‑* fraud headers
import { useMemo, useState } from "react";
import type { ClientFraudContext } from "@kordbooks/shared-hmrc-itsa-client";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "@/providers/auth-provider";

const DEVICE_ID_STORAGE_KEY = "fraud-device-id";

function safeHasWindow(): boolean {
    return typeof window !== "undefined";
}

function getOrCreateDeviceId(): string {
    if (!safeHasWindow()) {
        // In SSR/tests there is no window; just return a transient ID.
        return uuidv4();
    }

    try {
        const existing = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY);
        if (existing && existing.length > 0) {
            return existing;
        }

        const id = uuidv4();
        window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, id);
        return id;
    } catch {
        // If storage is unavailable (privacy mode, blocked, etc.), fall back to an in‑memory ID.
        return uuidv4();
    }
}

function getTimezoneOffsetUtcString(): string | undefined {
    try {
        const now = new Date();
        // Date.getTimezoneOffset is minutes *behind* UTC; invert for signed offset.
        const offsetMinutes = -now.getTimezoneOffset();
        const sign = offsetMinutes < 0 ? "-" : "+";
        const abs = Math.abs(offsetMinutes);
        const hours = String(Math.floor(abs / 60)).padStart(2, "0");
        const minutes = String(abs % 60).padStart(2, "0");

        // Added colon (:) between hours and minutes to match HMRC requirement
        return `UTC${sign}${hours}:${minutes}`;
    } catch {
        return undefined;
    }
}

function getScreens(): ClientFraudContext["screens"] {
    if (!safeHasWindow()) return undefined;

    try {
        const scr = window.screen;
        if (!scr) return undefined;

        const width = scr.width ?? 0;
        const height = scr.height ?? 0;
        const colourDepth = scr.colorDepth ?? 24;
        const scalingFactor = typeof window.devicePixelRatio === "number" && window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;

        return [
            {
                width,
                height,
                scalingFactor,
                colourDepth,
            },
        ];
    } catch {
        return undefined;
    }
}

function getWindowSizeSnapshot(): ClientFraudContext["windowSize"] {
    if (!safeHasWindow()) return undefined;

    try {
        return {
            width: window.innerWidth,
            height: window.innerHeight,
        };
    } catch {
        return undefined;
    }
}

/**
 * Collects device/user data needed for HMRC Gov‑Client‑* fraud headers.
 * This context should be POSTed to your worker, which will merge it with
 * vendor/infra data and call buildFraudHeaders.
 */
export function useFraudContext(): ClientFraudContext {
    const { user } = useAuth();
    const [deviceId] = useState(() => getOrCreateDeviceId());
    const [windowSize] = useState(() => getWindowSizeSnapshot());

    const fraudContext: ClientFraudContext = useMemo(() => {
        const userIds: Record<string, string> = {};

        if (user?.email) {
            userIds["login-id"] = user.email;
        }
        if (user?.id) {
            userIds["internal-id"] = user.id;
        }

        const hasUserIds = Object.keys(userIds).length > 0;

        const browserUserAgent = typeof navigator !== "undefined" && typeof navigator.userAgent === "string" ? navigator.userAgent : undefined;

        return {
            browserUserAgent,
            deviceId,
            // multiFactor can be filled in once MFA is wired into your auth flow.
            multiFactor: undefined,
            screens: getScreens(),
            timezone: getTimezoneOffsetUtcString(),
            userIds: hasUserIds ? userIds : undefined,
            windowSize,
            // publicIp / publicPort / publicIpTimestamp are intentionally omitted here;
            // they are set server‑side where they can be captured more reliably.
        };
    }, [deviceId, user, windowSize]);

    return fraudContext;
}
